/**
 * Paris V/N/D sur les matchs en direct, plus le marché « écart de buts » (le petit + à côté
 * de V et D : le camp l'emporte par 2, 3, 4… buts ou plus, issue `home+2`, `away+3`…).
 *
 * Trois surfaces partagent la même logique :
 *  - le panneau « Jeu » de la sidebar, qui reçoit déjà les cotes dans /competitions/{id}/live-matches ;
 *  - la page live d'un match de compétition, pour les spectateurs (matchBetWidget) ;
 *  - la page d'un match du module Ranking (authentique, LDM, Dream free), même widget
 *    avec kind = 'ranking'.
 *
 * La cote vue au moment du clic est renvoyée au serveur : s'il s'est passé quelque chose entre
 * les deux (but marqué, autre parieur), le pari est refusé et la nouvelle cote affichée plutôt
 * que d'engager le parieur sur un prix qu'il n'a pas vu.
 */
window.matchBetting = {
    // Repli seulement : les mises réellement acceptées arrivent avec la cote
    // (quote.stakes), le serveur restant seul juge (MatchBettingService::STAKES).
    stakes: [5000, 10000, 15000],

    /**
     * Base d'URL du marché : un match de compétition et un match du module Ranking
     * (authentique, LDM, Dream free) n'ont pas la même route.
     */
    endpoint(id, kind) {
        return kind === 'ranking' ? '/ranking/match/' + id + '/bets' : '/matches/' + id + '/bets';
    },

    /**
     * Une issue est `home` / `draw` / `away`, ou un écart `home+2`, `away+3`…
     * -> { side, margin } (margin null pour un V/N/D simple).
     */
    parseOutcome(outcome) {
        const m = /^(home|away)\+(\d+)$/.exec(outcome || '');
        if (m) return { side: m[1], margin: parseInt(m[2], 10) };
        return { side: outcome, margin: null };
    },

    /** Camp porté par une issue (couleur de la pastille). */
    sideOf(outcome) {
        return this.parseOutcome(outcome).side;
    },

    /** V, N, D, ou V +2 / D +3 pour un écart. */
    outcomeLabel(outcome) {
        const parsed = this.parseOutcome(outcome);
        const letter = { home: 'V', draw: 'N', away: 'D' }[parsed.side] || '?';
        return parsed.margin ? letter + ' +' + parsed.margin : letter;
    },

    /**
     * Entrée de cote d'une issue dans une quote : le trio `odds` pour un V/N/D, la liste
     * `margins[side]` pour un écart. Null si l'issue n'est pas (plus) proposée.
     */
    entryFor(quote, outcome) {
        if (!quote || !outcome) return null;
        const parsed = this.parseOutcome(outcome);
        if (!parsed.margin) return quote.odds?.[parsed.side] || null;
        return (quote.margins?.[parsed.side] || []).find(e => e.n === parsed.margin) || null;
    },

    /** Écarts proposés à un camp (vide sur un marché ouvert avant la mise en place). */
    marginsFor(quote, side) {
        return quote?.margins?.[side] || [];
    },

    /** Traduction (groupe `bets` exposé par le layout), repli sur la clé. */
    t(key, replacements) {
        return window.trans ? window.trans(key, replacements) : key;
    },

    /**
     * Une issue trop courte (match déjà plié) s'affiche mais ne se joue plus : elle ne
     * rendrait qu'un bénéfice sans risque, cf. BookmakerOddsService::MIN_BETTABLE_ODDS.
     * Marché ouvert avant la mise en place du drapeau : on laisse jouable.
     */
    isBettable(oddsEntry) {
        return oddsEntry?.bettable !== false;
    },

    /**
     * Le gain d'un ticket est plafonné : sur une grosse cote, seules les petites mises
     * sont offertes (cf. BookmakerOddsService::MAX_RETURN). Le plafond vient du serveur ;
     * sans lui (marché ouvert avant la mise en place), toutes les mises restent offertes.
     */
    stakeAllowed(stake, odds, maxReturn) {
        return !maxReturn || !odds || stake * odds <= maxReturn;
    },

    formatGp(amount) {
        return Number(amount || 0).toLocaleString('fr-FR');
    },

    /** « 10 000 » -> « 10k » pour les pastilles de mise. */
    shortGp(amount) {
        return (amount / 1000) + 'k';
    },

    csrf() {
        return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    },

    fetchQuote(matchId, kind) {
        return fetch(this.endpoint(matchId, kind), { cache: 'no-store' })
            .then(r => r.json())
            .then(data => (data.success ? data.quote : null));
    },

    place(matchId, outcome, stake, oddsSeen, kind) {
        return fetch(this.endpoint(matchId, kind), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.csrf()
            },
            body: JSON.stringify({ outcome: outcome, stake: stake, odds_seen: oddsSeen })
        }).then(r => r.json());
    }
};

/**
 * Widget de la page live (spectateurs). Interroge les cotes toutes les 20 s tant que le
 * marché est ouvert : le score bouge, la cote suit.
 */
window.matchBetWidget = function (matchId, kind) {
    return {
        matchId: matchId,
        kind: kind || 'match',
        quote: null,
        selected: null,
        // Camp dont la rangée d'écarts est dépliée (`home` / `away`), null sinon.
        marginsOpen: null,
        pending: false,
        message: null,
        messageType: 'info',
        timer: null,

        init() {
            this.refresh();
            this.timer = setInterval(() => this.refresh(), 20000);
            window.addEventListener('beforeunload', () => clearInterval(this.timer));
        },

        refresh() {
            window.matchBetting.fetchQuote(this.matchId, this.kind)
                .then(quote => { this.quote = quote; })
                .catch(err => console.error('Cotes indisponibles', err));
        },

        get stakes() {
            // Les mises viennent du serveur avec la cote ; la constante n'est qu'un repli.
            return this.quote?.stakes || window.matchBetting.stakes;
        },

        label(outcome) {
            return window.matchBetting.outcomeLabel(outcome);
        },

        side(outcome) {
            return window.matchBetting.sideOf(outcome);
        },

        t(key, replacements) {
            return window.matchBetting.t(key, replacements);
        },

        /** Entrée de cote (V/N/D ou écart) de l'issue demandée. */
        entry(outcome) {
            return window.matchBetting.entryFor(this.quote, outcome);
        },

        margins(side) {
            return window.matchBetting.marginsFor(this.quote, side);
        },

        /** Le + n'est proposé que si le serveur envoie des écarts pour ce camp. */
        hasMargins(side) {
            return this.margins(side).length > 0;
        },

        toggleMargins(side) {
            if (!this.quote?.open) return;
            this.marginsOpen = this.marginsOpen === side ? null : side;
            // Une sélection V/N/D ou un écart de l'autre camp n'a plus de sens ici.
            if (this.selected && window.matchBetting.parseOutcome(this.selected).side !== this.marginsOpen) {
                this.selected = null;
            }
            this.message = null;
        },

        gp(amount) {
            return window.matchBetting.formatGp(amount);
        },

        shortGp(amount) {
            return window.matchBetting.shortGp(amount);
        },

        oddsFor(outcome) {
            return this.entry(outcome)?.odds ?? null;
        },

        bookmakerFor(outcome) {
            return this.entry(outcome)?.bookmaker ?? '';
        },

        /**
         * Une issue déjà pliée s'affiche mais ne se joue plus (le serveur la refuse aussi).
         * Ancien marché sans le drapeau : on laisse jouable plutôt que de tout bloquer.
         */
        bettableFor(outcome) {
            return window.matchBetting.isBettable(this.entry(outcome));
        },

        /** Retour total (mise incluse) pour la mise survolée. */
        potentialReturn(outcome, stake) {
            const odds = this.oddsFor(outcome);
            return odds ? Math.round(stake * odds) : 0;
        },

        affordable(stake) {
            return this.quote?.budget === null || this.quote?.budget === undefined
                ? true
                : this.quote.budget >= stake;
        },

        /** Mise offerte sur l'issue sélectionnée, au regard du plafond de gain. */
        stakeAllowed(stake) {
            return window.matchBetting.stakeAllowed(stake, this.oddsFor(this.selected), this.quote?.max_return);
        },

        select(outcome) {
            if (!this.quote?.open) return;
            if (!this.bettableFor(outcome)) {
                this.message = 'Cette issue est trop courte pour être jouée.';
                this.messageType = 'info';
                return;
            }
            this.selected = this.selected === outcome ? null : outcome;
            // Un V/N/D simple referme la rangée d'écarts.
            if (this.selected && !window.matchBetting.parseOutcome(this.selected).margin) {
                this.marginsOpen = null;
            }
            this.message = null;
        },

        bet(stake) {
            if (!this.selected || this.pending) return;
            this.pending = true;
            const outcome = this.selected;

            window.matchBetting.place(this.matchId, outcome, stake, this.oddsFor(outcome), this.kind)
                .then(data => {
                    this.pending = false;
                    this.message = data.message;
                    this.messageType = data.success ? 'success' : 'error';
                    if (data.success) {
                        this.selected = null;
                        this.marginsOpen = null;
                        this.quote = data.quote;
                    } else if (data.odds_changed) {
                        // Le serveur renvoie le nouveau tableau de cotes : on le pousse
                        // directement pour que le parieur voie le prix à jour.
                        if (this.quote) {
                            this.quote.odds = data.odds;
                            if (data.margins) this.quote.margins = data.margins;
                        }
                    } else {
                        this.refresh();
                    }
                })
                .catch(err => {
                    this.pending = false;
                    this.message = 'Pari impossible pour le moment.';
                    this.messageType = 'error';
                    console.error(err);
                });
        }
    };
};
